import { apiRequest } from "./api-request";
import { logDiagnostic } from "./diagnostics";
import { CODES } from "./diagnostics/contract";

// Legacy SSE transport, now used ONLY for the deterministic @adr SPARK-0014-design-primitives-single-source@1 person-load endpoints —
// the chat turn moved to the AG-UI protocol (`agent-agui.ts`, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption). POSTs to
// the BFF /ai/sessions/* passthroughs, which inject the Bearer and pipe the agent-service
// Server-Sent Events back unchanged. We use fetch + a ReadableStream reader rather than
// EventSource: EventSource is GET-only and can't send the CSRF header or the request body.

export interface AgentEvent {
  kind: string;
  delta?: string;
  sessionId?: string;
  message?: string;
  suggestions?: string[];
  // Tool name on a `tool_call` event (e.g. "UpdateMyProfile"). Drives
  // Assistant-driven refetch — collected over the turn, mapped to queries in
  // lib/agent-refresh, refetched on `done`.
  name?: string;
}

// The agent-service serializes its events as a kotlinx sealed interface:
// the event variant lands in the JSON's polymorphic discriminator `type`
// (e.g. {"type":"token","delta":"…"}), while the `kind` getter is only on
// the SSE `event:` line. Normalize `type` → `kind` so callers can switch on
// `kind`. Tolerate `kind` too, in case the wire format changes.
interface RawAgentEvent {
  type?: string;
  kind?: string;
  delta?: string;
  sessionId?: string;
  message?: string;
  items?: string[];
  name?: string;
}

export class AgentStreamError extends Error {}

// An SSE event is terminated by a blank line: LF+LF, CR+CR, or CRLF+CRLF.
const FRAME_SEP = /\r\n\r\n|\r\r|\n\n/;

// Idle watchdog window for a person-load stream (spark#228 C3): abort after this
// many ms of zero bytes so a wedged upstream surfaces a client error instead of
// hanging. Generous — a deterministic person load streams promptly, but the
// window must never clip a healthy-but-slow one.
export const DEFAULT_STREAM_IDLE_MS = 45_000;

// POST to a BFF /ai* SSE endpoint through the shared request boundary (CSRF +
// correlation ID) and stream the events back through the shared frame parser.
// `path` is relative to basePath. JSON `body` (when present) sets Content-Type;
// absent → no body (path-only).
async function postStream(
  path: string,
  onEvent: (event: AgentEvent) => void,
  opts: { body?: string; signal?: AbortSignal; idleMs?: number } = {},
): Promise<void> {
  const { response, correlationId } = await apiRequest(path, {
    method: "POST",
    csrf: true,
    headers: {
      ...(opts.body !== undefined ? { "Content-Type": "application/json" } : {}),
      Accept: "text/event-stream",
    },
    body: opts.body,
    signal: opts.signal,
  });
  if (!response.ok || !response.body) {
    // apiRequest owns 5xx request diagnostics. This covers a missing stream
    // body and non-5xx response failures without logging response content.
    if ((!response.ok && response.status < 500) || (response.ok && !response.body)) {
      logDiagnostic({
        level: "error",
        code: CODES.http.STREAM_FAILURE,
        category: "http",
        details: { endpoint: streamEndpoint(path), method: "POST", status: response.status, phase: "response" },
      });
    }
    throw new AgentStreamError(`Agent stream failed (${response.status})`);
  }
  await pumpSse(response, onEvent, correlationId, opts.idleMs ?? DEFAULT_STREAM_IDLE_MS);
}

// Deterministic (no-LLM) person profile load (@adr SPARK-0014-design-primitives-single-source@1), streamed back as SSE like a turn — same
// event shape, so the caller reuses its streaming handler. Id rides in the path, so there's no body.
export function streamPersonDetail(
  sessionId: string,
  personId: string,
  onEvent: (event: AgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const path = `ai/sessions/${encodeURIComponent(sessionId)}/persons/${encodeURIComponent(personId)}`;
  return postStream(path, onEvent, { signal });
}

// Deterministic (no-LLM) comparison of several people (@adr SPARK-0014-design-primitives-single-source@1): POSTs the id list to the
// collection endpoint and streams back one Markdown table.
export function streamPersonsTable(
  sessionId: string,
  personIds: string[],
  onEvent: (event: AgentEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const path = `ai/sessions/${encodeURIComponent(sessionId)}/persons`;
  return postStream(path, onEvent, { body: JSON.stringify({ personIds }), signal });
}

// Read an SSE response body to completion, emitting one AgentEvent per frame.
// A stalled upstream (bytes stop, stream never closes) would otherwise pin
// reader.read() forever, so each read races an idle watchdog: after idleMs of
// silence the reader is cancelled and the stream fails like any interruption
// (spark#228 C3). The timer resets on every read that yields.
async function pumpSse(
  res: Response,
  onEvent: (event: AgentEvent) => void,
  correlationId?: string,
  idleMs: number = DEFAULT_STREAM_IDLE_MS,
): Promise<void> {
  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let malformedFrames = 0;
  let emittedEvents = 0;
  let terminalSeen = false;

  // Resolve `read` normally, or reject if idleMs elapses first. On idle, cancel
  // the reader so the underlying connection is torn down, then surface it as a
  // stream interruption via the shared catch below.
  const readWithIdle = (): Promise<ReadableStreamReadResult<Uint8Array>> =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reader.cancel().catch(() => {});
        reject(new AgentStreamError("Agent stream idle timeout"));
      }, idleMs);
      reader.read().then(
        (r) => {
          clearTimeout(timer);
          resolve(r);
        },
        (e) => {
          clearTimeout(timer);
          reject(e);
        },
      );
    });

  for (;;) {
    let read: ReadableStreamReadResult<Uint8Array>;
    try {
      read = await readWithIdle();
    } catch {
      logDiagnostic({
        level: "error",
        code: CODES.http.STREAM_FAILURE,
        category: "http",
        correlationId,
        details: { phase: "stream" },
      });
      throw new AgentStreamError("Agent stream interrupted");
    }
    const { done, value } = read;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE frames are separated by a blank line. The spec allows LF, CR, or
    // CRLF line endings, so match any blank-line form. We scan the accumulating
    // buffer (not the chunk) so a separator split across reads — e.g. a `\r\n`
    // ending one chunk and `\r\n` starting the next — is never matched
    // prematurely: a lone trailing `\r` or `\r\n` matches no alternative and is
    // kept for the next chunk. Process whole frames; keep the partial remainder.
    let m: RegExpExecArray | null;
    while ((m = FRAME_SEP.exec(buffer)) !== null) {
      const frame = buffer.slice(0, m.index);
      buffer = buffer.slice(m.index + m[0].length);
      const result = emitFrame(frame, onEvent);
      if (result === "malformed") malformedFrames++;
      if (result === "emitted") emittedEvents++;
      if (result === "terminal") {
        emittedEvents++;
        terminalSeen = true;
      }
    }
  }
  // Flush a final frame that arrived without a trailing blank line.
  if (buffer.trim()) {
    const result = emitFrame(buffer, onEvent);
    if (result === "malformed") malformedFrames++;
    if (result === "emitted") emittedEvents++;
    if (result === "terminal") {
      emittedEvents++;
      terminalSeen = true;
    }
  }
  if (malformedFrames > 0 || emittedEvents === 0 || !terminalSeen) {
    logDiagnostic({
      level: "error",
      code: CODES.http.STREAM_FAILURE,
      category: "http",
      correlationId,
      details: { phase: "stream" },
    });
  }
}

function emitFrame(
  frame: string,
  onEvent: (event: AgentEvent) => void,
): "ignored" | "malformed" | "emitted" | "terminal" {
  // A frame is a set of `field: value` lines. We only care about `data:`
  // — the JSON payload carries its own `kind`, so the `event:` line is
  // redundant for our purposes.
  const data = frame
    .split(/\r\n|\r|\n/)
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .join("\n");
  if (!data) return "ignored";
  try {
    const raw = JSON.parse(data) as RawAgentEvent;
    const kind = raw.kind ?? raw.type;
    if (!kind) return "malformed";
    onEvent({
      kind,
      delta: raw.delta,
      sessionId: raw.sessionId,
      message: raw.message,
      suggestions: raw.items,
      name: raw.name,
    });
    return kind === "done" ? "terminal" : "emitted";
  } catch {
    // Ignore malformed frames (keep-alive comments, partial JSON, etc.).
    return "malformed";
  }
}

function streamEndpoint(path: string): string {
  const parts = path.split("/");
  return parts
    .map((segment, index) =>
      index === 0 || segment === "stream" || segment === "sessions" || segment === "persons"
        ? segment
        : ":id",
    )
    .join("/");
}
